import { useState } from 'react';
import { useQuery } from './query';
import { OriginDestinationLogic } from './filters';

export const AirportType = {
  ORIGIN: 'ORIGIN',
  DESTINATION: 'DESTINATION'
};

export function useLocations({
  getAirports,
  prePopulation,
  integrationSettings,
  origin,
  destination,
  meta,
  pageContext,
  initialData,
  airportType = AirportType.ORIGIN,
  active = true,
  avoidCall = false
}) {
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const isActive =
    active &&
    (airportType === AirportType.ORIGIN ||
      origin ||
      (airportType === AirportType.DESTINATION && !origin) ||
      isFirstLoad);

  const getData = () => {
    if (avoidCall) {
      return Promise.resolve([]);
    }
    let dataPromise;
    if (isFirstLoad && initialData) {
      dataPromise = Promise.resolve(initialData);
    } else {
      const routeOption = { airportType };
      if (origin) {
        routeOption.selectedOrigin = origin;
      }
      if (destination) {
        routeOption.selectedDestination = destination;
      }
      let resultAirPort = { ...prePopulation.airport };

      if (prePopulation.airport?.filterOriginAirportsByMarket === true) {
        if (airportType !== AirportType.ORIGIN) {
          delete resultAirPort.filterFields;
        }
      } else if (
        prePopulation.airport?.filterByMarket === null ||
        prePopulation.airport?.filterByMarket === undefined
      ) {
        delete resultAirPort.filterFields;
      }

      dataPromise = getAirports(
        {
          ...resultAirPort,
          airlineIataCode: prePopulation.airlineIataCode,
          language: prePopulation.languageCode,
          routeOption
        },
        integrationSettings,
        meta,
        pageContext
      );
    }

    return dataPromise
      .then((data) => {
        setIsFirstLoad(false);
        return data;
      })
      .catch((err) => {
        setIsFirstLoad(false);
        throw err;
      });
  };

  return useQuery({ getData, active: isActive }, [
    integrationSettings,
    origin,
    destination,
    prePopulation.airlineIataCode,
    prePopulation.airport,
    prePopulation.languageCode,
    meta
  ]);
}

export function useOriginDestination({
  dispatch,
  origin,
  destination,
  showOrigin,
  showDestination,
  filter,
  initialOrigins,
  initialDestinations,
  originDestinationLogic,
  userInteraction,
  secondary
}) {
  const [originSelected, setOriginSelected] = useState(origin.value);
  const [destinationSelected, setDestinationSelected] = useState(destination.value);

  const avoidCall = originDestinationLogic === OriginDestinationLogic.SAME && !userInteraction;
  let parameters = {
    ...filter,
    active: showOrigin,
    airportType: AirportType.ORIGIN,
    initialData: initialOrigins
  };
  if (originDestinationLogic.toLowerCase() !== OriginDestinationLogic.SAME) {
    parameters['destination'] = secondary ? destinationSelected : destination.value;
  }
  const origins = useLocations(parameters);

  let destinations = useLocations({
    ...filter,
    active: showDestination,
    airportType: AirportType.DESTINATION,
    origin: secondary ? originSelected : origin.value,
    initialData: initialDestinations,
    avoidCall
  });
  if (avoidCall || destinations.data.length === 0) {
    destinations = { ...origins };
  }

  const clearOrigin = () => {
    if (originSelected) {
      setOriginSelected(undefined);
      setDestinationSelected(undefined);
    }
    dispatch({ type: 'ORIGIN_CLEAR' });
    if (secondary) {
      dispatch({ type: 'ORIGIN_CLEAR_SECONDARY' });
    }
  };

  const clearDestination = () => {
    if (destinationSelected) {
      setDestinationSelected(undefined);
    }
    dispatch({ type: 'DESTINATION_CLEAR' });
    if (secondary) {
      dispatch({ type: 'DESTINATION_CLEAR_SECONDARY' });
    }
  };
  const selectOrigin = ({ value }) => {
    dispatch({ type: 'SET_USER_INTERACTION' });
    if (destinationSelected === value) {
      setDestinationSelected(undefined);
    }
    setOriginSelected(value);
  };
  const selectDestination = ({ value }) => {
    dispatch({ type: 'SET_USER_INTERACTION' });
    setDestinationSelected(value);
  };

  const onChangeOrigin = (newOrigin) => {
    dispatch({ type: 'ORIGIN_CHANGE', payload: newOrigin });
  };

  const onChangeDestination = (newDestination) => {
    dispatch({
      type: 'DESTINATION_CHANGE',
      payload: newDestination
    });
  };

  const swapLocations = () => {
    dispatch({ type: 'SWAP_LOCATIONS' });
  };
  // [values, config]
  return {
    origin: {
      ...origins,
      value: origin.value,
      valueSelect: originSelected,
      selectOrigin,
      onChange: onChangeOrigin,
      onClear: clearOrigin
    },
    destination: {
      ...destinations,
      value: destination.value,
      valueSelect: destinationSelected,
      selectDestination,
      onChange: onChangeDestination,
      onClear: clearDestination
    },
    swapLocations
  };
}
